// Catalog.ID Client — Onboarding Resume Stash
// ============================================================================
// localStorage record of an in-flight registration (registration-restructure
// §3.3): the wizard's shared state — THROWAWAY credentials included — so a
// crashed or interrupted registration can resume, on this browser profile,
// until the print gate passes.
//
// Deliberately unencrypted (decided 2026-09-01): the credentials it holds are
// disposable — finalize-at-print replaces them with freshly minted ones that
// never touch disk — and the server purges never-finalized accounts, so an
// abandoned stash unlocks nothing of value. A PIN would only add a forgettable
// credential and a brute-forceable speed bump.
//
// Lifecycle: written from the keygen step onward (save() refreshes savedAt, so
// an active wizard never expires mid-flight); wiped when the type-back print
// gate passes, when the user discards the registration, or by TTL (7 days,
// aligned with the server-side incomplete-onboarding purge).

import { MAX_CARD_EXTRA_COPIES } from '../crypto/recovery-kit';
import { mintRecoveryKitEntry, recoveryKitBagEntry } from '../crypto/recovery-kit-entry';
import type { KeyBagEntry } from '../crypto/keybag';

const STASH_KEY = 'cid_onboarding_stash_v1';

export const ONBOARDING_STASH_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface OnboardingStash {
    v: 1;
    /** ms epoch of the last save — refreshed on every save(). */
    savedAt: number;
    /** Which wizard owns this record. `delegate-activation` (round 6, §13.3)
     *  has no payment leg — an invited delegate is paid for by the organization
     *  — and carries `invitationCode` instead of a registrationId/voucher. */
    registrationType: 'personal' | 'organization' | 'delegate-activation';
    /** Signed invitation (registration-restructure §14.2, M18): the 16-character
     *  CODE printed on the card. The lookup id and the fixed-value salt are both
     *  one scrypt away from it, so keeping the code — rather than either
     *  derivative — is what lets a resumed wizard carry on without asking the
     *  invitee to type it again. Unencrypted, like the rest of this record (§3.3),
     *  and no more exposed than the card in their pocket.
     *
     *  The fixed email/phone are deliberately NOT here: the invitation fixes them
     *  as digests, the card prints them in ink, and the member types them — one
     *  invitation behaves one way however it was opened (§14.2). */
    invitationCode?: string;
    /** Payment leg (worker re-serves the proof against these within its TTL). */
    registrationId?: string;
    nonce?: string;
    /** Voucher leg: the code being redeemed. Unlike the proof token this does NOT
     *  expire in an hour, so resuming a day later still works — re-begin with the
     *  same code returns the same reserved CID (registration-restructure §7.2). */
    voucherCode?: string;
    /** Org registrations: the personal session token for the resumed
     *  /api/organization/register call. */
    personalSessionToken?: string;
    /** Finalize (re-encrypt/set-authhash) has begun — resume must land on the
     *  finalize step and mint FRESH final credentials. */
    finalizeStarted?: boolean;
    /** Issued Account Unlock Sheet (base64 sealed token) — reprints must REUSE it:
     *  re-issuing bumps the epoch and kills the already-printed sheet. */
    resetCardSealedToken?: string;
    resetCardCreatedAt?: string;
    /** The node already holds this registration's Authenticator Key
     *  (registration-restructure §15.6, round 8). Same posture as the Unlock
     *  Sheet token above and for the same reason: a second
     *  `PUT /api/account/totp/key` rotates the secret on the node and voids the
     *  sheet that has already been printed, so a resume must never re-PUT. The
     *  secret itself rides `shared.totpSecret` (base32) — final material, not
     *  throwaway, but wiped with the rest of the stash at the print gate. */
    totpStored?: boolean;
    /**
     * Snapshot of the wizard's `shared` map.
     *
     * Round 10's money-leg bookkeeping lives HERE rather than as top-level
     * fields (registration-wallets §7's "Onboarding stash"), because it is
     * wizard state like the rest and an organization registration needs TWO
     * sets of it. Read it back with {@link readBitsStash}:
     *
     *   bitsReleased         the lock was drained into this account's wallet
     *   bitsWalletRef        the DO ref of that wallet — reads a balance, spends
     *                        nothing, so it is safe in an unencrypted record
     *   bitsFundAddress      the receive address the kit sheet prints
     *   defaultTokenIssued   the `catalog:default` token exists
     *   publishFundAddress   the member ticked the publish box and it took
     *   bitcashWalletLabel   the label the bag entry carries
     *
     * and the same six prefixed `org` for the ORGANIZATION's own wallet.
     *
     * NOT here, deliberately: no code, no seed, no lock key and no bits figure.
     * The lock keys derive from `voucherCode`/`invitationCode`, which this
     * record already holds, and duplicating them would be a second place for a
     * money-bearing value to leak out of an unencrypted store.
     */
    shared: Record<string, unknown>;
}

// ── round 10: the money leg's bookkeeping (registration-wallets §4.5, §7) ────

/** What a print step wrote down about one wallet. */
export interface BitsStash {
    /** Resume guard. Re-releasing is harmless (the Worker answers the
     *  idempotent 200), so this is a courtesy rather than a protection. */
    bitsReleased: boolean;
    bitsWalletRef?: string;
    bitsFundAddress?: string;
    /** Resume guard that DOES protect something: every AUTH token past the
     *  first costs bits, so a resume must never mint a second default token.
     *  A crash between the Worker call and this flag is repaired on the next
     *  bag-open by ADOPTING the active `catalog:default` token (§4.12). */
    defaultTokenIssued: boolean;
    publishFundAddress: boolean;
    bitcashWalletLabel?: string;
}

function flag(value: unknown): boolean {
    // Only an explicit `true`. A truthy string from a hand-edited stash must
    // not be able to skip a token mint.
    return value === true;
}

function text(value: unknown): string | undefined {
    return typeof value === 'string' && value ? value : undefined;
}

/**
 * Read one wallet's money-leg fields out of a `shared` snapshot.
 *
 * `prefix` selects the set: none for the account's own wallet (a personal
 * registration, or the founding delegate's in an organization one), `'org'` for
 * the ORGANIZATION's. An organization registration runs the leg twice against
 * two different wallets and two different locks (§4.13), and one set of fields
 * would have the second overwrite the first.
 *
 * Every flag defaults to FALSE: a missing field must mean "not done", so a
 * pre-round-10 stash re-runs the step rather than skipping it.
 */
export function readBitsStash(
    shared: Record<string, unknown>, prefix: '' | 'org' = '',
): BitsStash {
    const key = (name: string): string =>
        prefix ? prefix + name.charAt(0).toUpperCase() + name.slice(1) : name;
    return {
        bitsReleased: flag(shared[key('bitsReleased')]),
        bitsWalletRef: text(shared[key('bitsWalletRef')]),
        bitsFundAddress: text(shared[key('bitsFundAddress')]),
        defaultTokenIssued: flag(shared[key('defaultTokenIssued')]),
        publishFundAddress: flag(shared[key('publishFundAddress')]),
        bitcashWalletLabel: text(shared[key('bitcashWalletLabel')]),
    };
}

let storageOverride: Storage | null = null;

/** Test seam: swap the backing storage (vitest runs without a DOM). */
export function _setStashStorageForTests(s: Storage): void {
    storageOverride = s;
}

function store(): Storage | null {
    if (storageOverride) return storageOverride;
    try {
        return globalThis.localStorage;
    } catch {
        return null; // storage access can throw (privacy modes, non-DOM environments)
    }
}

/**
 * Wizard state that NEVER reaches this unencrypted record (docs/cid-governance.md
 * M15, the M11 probe): the organization's throwaway password. Whoever read it
 * held an organization session — for the organization bound to a node's code,
 * one with operator powers. The wizard no longer keeps it in `shared` at all
 * (it lives only for the registration call); this is the belt to that pair of
 * braces. The delegate's throwaway stays: a resume logs in with it, and finalize
 * replaces it.
 */
const NEVER_STASHED: readonly string[] = ['orgPassword'];

export function saveOnboardingStash(stash: OnboardingStash): void {
    const s = store();
    if (!s) return;
    try {
        const shared = { ...stash.shared };
        for (const key of NEVER_STASHED) delete shared[key];
        s.setItem(STASH_KEY, JSON.stringify({ ...stash, shared, savedAt: Date.now() }));
    } catch {
        // Quota/permission failure — the wizard continues; only resume is lost.
    }
}

export function loadOnboardingStash(): OnboardingStash | null {
    const s = store();
    if (!s) return null;
    const raw = s.getItem(STASH_KEY);
    if (!raw) return null;

    let parsed: OnboardingStash;
    try {
        parsed = JSON.parse(raw) as OnboardingStash;
    } catch {
        s.removeItem(STASH_KEY);
        return null;
    }
    if (parsed.v !== 1 || typeof parsed.savedAt !== 'number' || typeof parsed.shared !== 'object') {
        s.removeItem(STASH_KEY);
        return null;
    }
    if (Date.now() - parsed.savedAt > ONBOARDING_STASH_TTL_MS) {
        s.removeItem(STASH_KEY);
        return null;
    }
    return parsed;
}

export function clearOnboardingStash(): void {
    store()?.removeItem(STASH_KEY);
}

// ── recovery-kit choices ────────────────────────────────────────────────────
// The kit step's answers live in the stash's `shared` map like the rest of the
// wizard state. Since the review of 2026-09-02 that is a preset id, per-sheet
// purposes AND per-sheet ADDITIONAL-copy counts — and that count decides how many
// extra sheets the single print job emits, so the resume path validates it
// rather than trusting whatever is in localStorage (unencrypted by design).

export interface OnboardingKitChoices {
    /** Only an explicit `false` means "no recovery sheets". */
    optIn: boolean;
    /** Preset the labels/copies were prefilled from, when one was picked. */
    presetId?: string;
    thresholdK?: number;
    totalSharesN?: number;
    labels: string[];
    /** ADDITIONAL printed copies per sheet type (beyond the one in the master
     *  set), each within 0..MAX_CARD_EXTRA_COPIES. */
    extraCopies: number[];
}

function clampExtraCopies(value: unknown): number {
    const n = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : 0;
    return Math.min(MAX_CARD_EXTRA_COPIES, Math.max(0, n));
}

/** Read the kit step's answers back out of a `shared` snapshot, normalized. */
export function readKitChoices(shared: Record<string, unknown>): OnboardingKitChoices {
    const rawLabels = shared['kitLabels'];
    const rawExtraCopies = shared['kitExtraCopies'];
    return {
        optIn: shared['kitOptIn'] !== false,
        presetId: typeof shared['kitPresetId'] === 'string' ? shared['kitPresetId'] : undefined,
        thresholdK: typeof shared['kitThresholdK'] === 'number' ? shared['kitThresholdK'] : undefined,
        totalSharesN: typeof shared['kitTotalSharesN'] === 'number' ? shared['kitTotalSharesN'] : undefined,
        labels: Array.isArray(rawLabels) ? rawLabels.map(v => (typeof v === 'string' ? v : '')) : [],
        extraCopies: Array.isArray(rawExtraCopies) ? rawExtraCopies.map(clampExtraCopies) : [],
    };
}

/** Write the kit step's answers into `shared` (the caller then stash-syncs). */
export function writeKitChoices(shared: Record<string, unknown>, choices: OnboardingKitChoices): void {
    shared['kitOptIn'] = choices.optIn;
    shared['kitPresetId'] = choices.presetId;
    shared['kitThresholdK'] = choices.thresholdK;
    shared['kitTotalSharesN'] = choices.totalSharesN;
    shared['kitLabels'] = choices.labels;
    shared['kitExtraCopies'] = choices.extraCopies.map(clampExtraCopies);
}

// ── Round 9: the kit choices mint the SET (§16.6) ───────────────────────────

/**
 * Mint the `recovery_kit` bag entry the finalize step writes, from the choices
 * the opt-in step stashed. Null when the member opted out.
 *
 * Minted here rather than inside the kit builder because the SET has to reach
 * the bag the node stores — and the bundle the kit exports — before anything is
 * printed. `defaults` is the wizard's profile when a pre-round-9 stash (or an
 * opt-in with no preset picked) names none: 2-of-3 personal, 3-of-5
 * organization.
 *
 * RESUME: the caller passes the result to `finalizeCredentials`, which appends
 * it ONLY when the bag holds no active kit entry — so a resumed finalize prints
 * the set that is already on the server and this mint is discarded.
 */
export function kitEntryFromChoices(
    choices: OnboardingKitChoices,
    defaults: { thresholdK: number; totalSharesN: number },
    now?: Date,
): KeyBagEntry | null {
    if (!choices.optIn) return null;
    return recoveryKitBagEntry(mintRecoveryKitEntry({
        thresholdK: choices.thresholdK ?? defaults.thresholdK,
        totalSharesN: choices.totalSharesN ?? defaults.totalSharesN,
        labels: choices.labels,
        now,
    }));
}

/**
 * The stashed per-sheet ADDITIONAL copy counts, or undefined when they do not
 * line up with the set actually being printed (a stash written against another
 * preset, or a pre-round-9 one). The kit builder refuses a mis-sized array, and
 * a print-only preference must never be what fails a finalize.
 */
export function kitExtraCopiesFor(
    choices: OnboardingKitChoices, totalSharesN: number,
): number[] | undefined {
    return choices.extraCopies.length === totalSharesN ? choices.extraCopies : undefined;
}

/** The account an unfinished registration has already created on the node. */
export interface StashedAccount {
    kind: 'personal' | 'organization' | 'delegate';
    cid: string;
}

/**
 * The account this stash's wizard has already created, or null before it
 * exists — read from the field each wizard writes the moment the node answers
 * (`cid` at the personal Completion step, `orgCid` at the organization's,
 * `delegateCid` after activation). Once it exists, the stash is the only way to
 * finish it (auth/wizard-exit.ts), so discarding it costs the account.
 */
export function stashedAccount(stash: OnboardingStash): StashedAccount | null {
    const shared = stash.shared ?? {};
    const named = (key: string): string | null => {
        const v = shared[key];
        return typeof v === 'string' && v !== '' ? v : null;
    };
    switch (stash.registrationType) {
        case 'personal': {
            const cid = named('cid');
            return cid ? { kind: 'personal', cid } : null;
        }
        case 'organization': {
            const cid = named('orgCid');
            return cid ? { kind: 'organization', cid } : null;
        }
        case 'delegate-activation': {
            const cid = named('delegateCid');
            return cid ? { kind: 'delegate', cid } : null;
        }
    }
}
